/**
 * AppShell — Top-level provider wrapper for dashboard apps.
 * Wraps QueryClient, Router, and Theme providers.
 * @layer layout
 * @package @plerity/dashboard-shared
 */

import { createStandardQueryClient } from "@plerity/data-access";
import { QueryClientProvider } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useState } from "react";
import { BrowserRouter, createBrowserRouter, RouterProvider } from "react-router-dom";

const defaultQueryClient = createStandardQueryClient();

/**
 * Router flavour.
 *
 * - `"browser"` (default) — classic `<BrowserRouter>`. Unchanged fleet behaviour.
 * - `"data"` — `createBrowserRouter` + `<RouterProvider>` with a single splat
 *   (`path: "*"`) root route whose element is `children`. All descendant
 *   `<Routes>` keep working exactly as before (same `v7_relativeSplatPath`
 *   semantics), but data-router-only APIs — notably `useBlocker` (unsaved-
 *   changes prompts) — become available. This is React Router's documented
 *   incremental migration path from BrowserRouter.
 */
export type AppShellRouterMode = "browser" | "data";

export interface AppShellProps {
  children: ReactNode;
  /** Custom QueryClient instance (uses default if omitted) */
  queryClient?: ReturnType<typeof createStandardQueryClient>;
  /** Base path for the router (e.g. '/apps/seo/') */
  basename?: string;
  /** Router flavour (default `"browser"`). See {@link AppShellRouterMode}. */
  router?: AppShellRouterMode;
}

/**
 * Carries the (re-rendered) children into the data router's single root
 * route. The router itself is created once; its route element reads the
 * latest children from this context, so parent re-renders still flow down.
 */
const DataRouterChildrenContext = createContext<ReactNode>(null);

function DataRouterChildren() {
  return <>{useContext(DataRouterChildrenContext)}</>;
}

function DataRouterShell({ children, basename }: { children: ReactNode; basename?: string }) {
  // Created once per mount. Not disposed on unmount on purpose: StrictMode's
  // dev double-effect would dispose the live router; an app-lifetime router
  // leaks nothing meaningful (one popstate listener).
  const [router] = useState(() =>
    createBrowserRouter([{ path: "*", element: <DataRouterChildren /> }], {
      basename,
      future: {
        v7_relativeSplatPath: true,
        v7_fetcherPersist: true,
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_skipActionErrorRevalidation: true,
      },
    })
  );
  return (
    <DataRouterChildrenContext.Provider value={children}>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </DataRouterChildrenContext.Provider>
  );
}

export function AppShell({ children, queryClient = defaultQueryClient, basename, router = "browser" }: AppShellProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {router === "data" ? (
        <DataRouterShell basename={basename}>{children}</DataRouterShell>
      ) : (
        <BrowserRouter basename={basename} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          {children}
        </BrowserRouter>
      )}
    </QueryClientProvider>
  );
}
